import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';
import { CommuniquesService } from '../communiques.service';
import { Communique } from '../communiques-list/communiques-list.component';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

@Component({
  selector: 'app-communiques-detail',
  standalone: false,
  templateUrl: './communiques-detail.component.html',
  styleUrls: ['./communiques-detail.component.scss'],
})
export class CommuniquesDetailComponent implements OnInit, OnDestroy {
  private route     = inject(ActivatedRoute);
  private router    = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private service   = inject(CommuniquesService);
  private destroy$ = new Subject<void>();

  communique  = signal<Communique | null>(null);
  isLoading   = signal(true);
  errorMessage = signal<string | null>(null);

  pendingStatus = signal<{ status: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal(false);
  isSaving      = signal(false);

  safePdfUrl = computed((): SafeResourceUrl | null => {
    const rawLink = this.communique()?.pdf_link;
    if (!rawLink) return null;
    // The backend's signRawUrl may use a wrong APP_URL host. Rewrite the origin to
    // the known API base URL so the request always hits the backend proxy route
    // (/api/files/raw/*) which strips the file-service CSP frame-ancestors header.
    let url = rawLink;
    try {
      const parsed = new URL(rawLink);
      const apiBase = new URL(environment.apiUrl);
      parsed.hostname = apiBase.hostname;
      parsed.protocol = apiBase.protocol;
      parsed.port = apiBase.port;
      url = parsed.toString();
    } catch {}
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  fileExt = computed((): string => {
    const name = this.communique()?.pdf_name || this.communique()?.pdf_link || '';
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  });

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.service.getCommuniqueById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => { this.communique.set(res.data || res); this.isLoading.set(false); },
      error: () => { this.errorMessage.set('Impossible de charger ce communiqué'); this.isLoading.set(false); },
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  goBack(): void { this.router.navigate(['/federation/communiques']); }
  onEdit(): void { this.router.navigate(['/federation/communiques', this.communique()!.id_press_release, 'edit']); }

  confirmStatus(status: 'ACTI' | 'INAC'): void { this.pendingStatus.set({ status }); }
  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const c = this.communique();
    const pending = this.pendingStatus();
    if (!c || !pending) return;
    this.isSaving.set(true);
    this.pendingStatus.set(null);
    this.service.setStatus(c.id_press_release, pending.status).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.communique.update(prev => prev ? { ...prev, status: pending.status } : prev);
        this.isSaving.set(false);
        const label = pending.status === 'ACTI' ? 'En ligne' : 'Hors ligne';
        SwalUtils.success('Statut mis à jour', `Le communiqué est maintenant ${label}.`);
      },
      error: () => { SwalUtils.error('Erreur', 'Impossible de mettre à jour le statut.'); this.isSaving.set(false); },
    });
  }

  confirmDelete(): void { this.pendingDelete.set(true); }
  cancelDelete(): void { this.pendingDelete.set(false); }

  applyDelete(): void {
    const c = this.communique();
    if (!c) return;
    this.isSaving.set(true);
    this.pendingDelete.set(false);
    this.service.setStatus(c.id_press_release, 'ARCH').pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.router.navigate(['/federation/communiques']),
      error: () => { SwalUtils.error('Erreur', 'Impossible d\'archiver ce communiqué.'); this.isSaving.set(false); },
    });
  }

  statusLabel(status: string): string {
    return ({ ACTI: 'En ligne', INAC: 'Hors ligne', ARCH: 'Archivé' } as any)[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    if (status === 'ARCH') return 'bg-stone-100 text-stone-500 border-stone-200';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  pendingStatusLabel(status: 'ACTI' | 'INAC'): string {
    return ({ ACTI: 'Publier', INAC: 'Désactiver' } as any)[status];
  }
}
